import { useForm } from 'react-hook-form'

import { useManagementGetContactActions, useManagementGetContacts } from '@apps-common/apis/management/contact'
import { useManagementCancelUnitActive } from '@apps-common/apis/management/unit-active'
import { ButtonRounded } from '@apps-common/components/elements/button'
import { SelectInputSM, TextAreaSM } from '@apps-common/components/elements/form'
import { ModalBody, ModalContainer, ModalFooter, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { IActiveCancelModel } from '@apps-common/types/management/unit'
import { IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'

export const CancelUnitModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: { unit: IUnitGenericDetailsModel }
}) => {
  const { unit } = componentProps
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<IActiveCancelModel>({
    defaultValues: {
      activeUnitId: unit.unitId,
      contactNote: ''
    }
  })

  const { mutate, isPending } = useManagementCancelUnitActive(true, () => onDismiss(null, ModalDismiss.Confirm))
  const {
    isLoading: contactsLoading,
    isError: contactsError,
    data: contacts
  } = useManagementGetContacts({
    search: ''
  })
  const { isLoading: contactActionsLoading, isError: contactActionsError, data: contactActions } = useManagementGetContactActions({})

  const onSubmit = (values: IActiveCancelModel) => mutate({ unitId: unit.unitId, model: values })

  const loading = contactsLoading || contactsError || contactActionsLoading || contactActionsError
  const updating = isSubmitting || isPending

  return (
    <ModalContainer innerClasses='!h-auto !min-h-0 sm:!min-h-0 !max-w-[700px] !bg-secondary'>
      <ModalHeaderContainer classes='!bg-secondary/30'>
        <div className='flex items-start justify-between gap-[10px]'>
          <ModalHeader subtitle='Update' title='Cancel device' subtitleClasses='capitalize' />
          <button
            type='button'
            aria-label='Close filter'
            className='bg-secondary-content/5 text-secondary-content text-[1.2em] w-[35px] h-[35px] rounded-full flex items-center justify-center'
            onClick={() => {
              onDismiss(null, ModalDismiss.Confirm)
            }}
          >
            <commonIcons.close />
          </button>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[120px] !pb-[100px] sm:!pb-[70px] w-full'>
        <form noValidate id='cancelUnitForm' onSubmit={handleSubmit(onSubmit)}>
          <div className='grid lg:grid-cols-2 gap-x-[20px]'>
            <SelectInputSM
              type='text'
              placeholder='Select Contact'
              label='Contact'
              id='contactId'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('contactId', {
                required: {
                  value: true,
                  message: 'Required'
                }
              })}
              options={(contacts || []).map((c) => ({ label: c.name, value: c.contactId }))}
              errors={errors.contactId}
            />
            <SelectInputSM
              type='text'
              placeholder='Select Action'
              label='Contact Action'
              id='contactNote'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('contactNote', {
                required: {
                  value: true,
                  message: 'Required'
                }
              })}
              options={(contactActions || []).map((c) => ({ label: c.action, value: c.action }))}
              errors={errors.contactNote}
            />
          </div>
          <TextAreaSM
            type='text'
            placeholder='Enter a note relating to this cancellation...'
            label='Note'
            id='note'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('note', {
              required: false
            })}
            errors={errors.note}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-[10px]'>
          <ButtonRounded
            form='cancelUnitForm'
            type='submit'
            text='Submit'
            loading={updating}
            className='!h-[30px] min-h-0 !min-w-[120px] mt-0 !w-auto'
          />
          <ButtonRounded
            type='button'
            text='Cancel'
            loading={false}
            className='!h-[30px] min-h-0 !min-w-[120px] mt-0 !w-auto'
            callback={() => onDismiss(null, 'cancel')}
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
